import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Users, Trash2, Crown, Clock, Circle, Shield, UserPlus, UserMinus, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamsPage() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [pendingTeams, setPendingTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [availOpen, setAvailOpen] = useState(false);
  const [availGames, setAvailGames] = useState([]);
  const [availDisplay, setAvailDisplay] = useState(true);
  const [availTimezone, setAvailTimezone] = useState('EST');
  const [formData, setFormData] = useState({
    name: '',
    tag: '',
    description: ''
  });

  const TIMEZONES = ['EST', 'CST', 'MST', 'PST', 'AST', 'HST', 'AKST', 'GMT', 'CET', 'EET', 'AEST', 'JST'];

  const to12hr = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hr12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hr12}:${m} ${ampm}`;
  };

  useEffect(() => {
    fetchTeams();
    if (isAdmin) fetchPendingTeams();
  }, [isAdmin]);

  const fetchTeams = async () => {
    try {
      const { data } = await axios.get(`${API}/teams`);
      setTeams(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTeams = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/teams/pending`);
      setPendingTeams(data);
    } catch (err) {
      console.error('Failed to fetch pending teams:', err);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API}/teams`, formData);
      toast.success('Team registered! Awaiting admin approval.');
      setCreateOpen(false);
      setFormData({ name: '', tag: '', description: '' });
      fetchTeams();
      if (isAdmin) fetchPendingTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success('Team deleted');
      fetchTeams();
      if (isAdmin) fetchPendingTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleApproveTeam = async (teamId) => {
    try {
      await axios.put(`${API}/admin/teams/${teamId}/approve`);
      toast.success('Team approved!');
      fetchTeams();
      fetchPendingTeams();
    } catch (err) {
      toast.error('Failed to approve team');
    }
  };

  const handleDeclineTeam = async (teamId) => {
    try {
      await axios.put(`${API}/admin/teams/${teamId}/decline`);
      toast.success('Team declined');
      fetchPendingTeams();
    } catch (err) {
      toast.error('Failed to decline team');
    }
  };

  const openTeamDetails = async (team) => {
    setSelectedTeam(team);
    setMembersLoading(true);
    setTeamMembers([]);
    try {
      const { data } = await axios.get(`${API}/teams/${team.id}/members`);
      setTeamMembers(data.members);
    } catch (err) {
      console.error('Error fetching members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const isTeamLeader = (team) => user?.id === team?.captain_id;

  const openAddPlayer = async () => {
    setAddPlayerOpen(true);
    setPlayerSearch('');
    try {
      const { data } = await axios.get(`${API}/players`);
      setAllPlayers(data);
    } catch (err) { console.error('Failed to load players:', err); setAllPlayers([]); }
  };

  const handleAddPlayer = async (playerId) => {
    if (!selectedTeam) return;
    try {
      const { data } = await axios.post(`${API}/teams/${selectedTeam.id}/roster/add`, { user_id: playerId });
      toast.success('Player added to roster!');
      setSelectedTeam(data);
      setAddPlayerOpen(false);
      openTeamDetails(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add player');
    }
  };

  const handleRemovePlayer = async (playerId) => {
    if (!selectedTeam) return;
    try {
      const { data } = await axios.post(`${API}/teams/${selectedTeam.id}/roster/remove`, { user_id: playerId });
      toast.success('Player removed from roster');
      setSelectedTeam(data);
      openTeamDetails(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove player');
    }
  };

  const openAvailability = () => {
    const a = selectedTeam?.availability || {};
    setAvailGames(a.games || []);
    setAvailDisplay(a.display !== false);
    setAvailTimezone(a.timezone || 'EST');
    setAvailOpen(true);
  };

  const toggleGame = (gameNum) => {
    const exists = availGames.find(g => g.game === gameNum);
    if (exists) {
      setAvailGames(availGames.filter(g => g.game !== gameNum));
    } else {
      setAvailGames([...availGames, { game: gameNum, time: '' }]);
    }
  };

  const updateGameTime = (gameNum, time) => {
    setAvailGames(availGames.map(g => g.game === gameNum ? { ...g, time } : g));
  };

  const handleSaveAvailability = async (e) => {
    e.preventDefault();
    if (!selectedTeam) return;
    const filtered = availGames.filter(g => g.time);
    try {
      await axios.put(`${API}/teams/${selectedTeam.id}/availability`, { games: filtered, display: availDisplay, timezone: availTimezone });
      toast.success('Availability updated');
      setAvailOpen(false);
      fetchTeams();
      setSelectedTeam({ ...selectedTeam, availability: { games: filtered, display: availDisplay, timezone: availTimezone } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleRemoveAvailability = async () => {
    if (!selectedTeam) return;
    try {
      await axios.delete(`${API}/teams/${selectedTeam.id}/availability`);
      toast.success('Availability removed');
      setAvailOpen(false);
      fetchTeams();
      const updated = { ...selectedTeam };
      delete updated.availability;
      setSelectedTeam(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const filteredAddPlayers = allPlayers.filter(p =>
    p.id !== user?.id &&
    !teamMembers.some(m => m.id === p.id) &&
    p.name.toLowerCase().includes(playerSearch.toLowerCase())
  );

  const userTeam = teams.find(t => t.captain_id === user?.id) || pendingTeams.find(t => t.captain_id === user?.id);

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="teams-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-2">
              Registered Teams
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white">
              Competing Squads
            </h1>
          </div>

          {isAuthenticated && !userTeam && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none"
                  data-testid="create-team-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Register Team
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="create-team-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-white uppercase">
                    Register Your Team
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTeam} className="space-y-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                      Team Name
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Shadow Wolves"
                      required
                      className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                      data-testid="team-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                      Team Tag (3-5 chars)
                    </Label>
                    <Input
                      value={formData.tag}
                      onChange={(e) => setFormData({...formData, tag: e.target.value.toUpperCase().slice(0, 5)})}
                      placeholder="e.g., SHW"
                      required
                      maxLength={5}
                      minLength={2}
                      className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 font-mono"
                      data-testid="team-tag-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                      Description (Optional)
                    </Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Tell us about your team..."
                      className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[100px] focus:border-cyan-400 resize-none"
                      data-testid="team-description-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
                    data-testid="submit-team-button"
                  >
                    {creating ? 'Creating...' : 'Create Team'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {!isAuthenticated && (
            <Button 
              className="bg-zinc-800 text-zinc-400 cursor-not-allowed rounded-none"
              disabled
              data-testid="login-to-register-button"
            >
              Login to Register
            </Button>
          )}
        </div>

        {/* User's pending team notice */}
        {userTeam && userTeam.status === 'pending' && !isAdmin && (
          <div className="mb-8 p-4 bg-yellow-500/10 border border-yellow-500/30" data-testid="pending-team-notice">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div>
                <p className="text-yellow-500 font-bold">Your team [{userTeam.tag}] {userTeam.name} is pending approval</p>
                <p className="text-zinc-400 text-sm">An admin will review your team registration shortly.</p>
              </div>
            </div>
          </div>
        )}

        {/* Admin: Pending Teams */}
        {isAdmin && pendingTeams.length > 0 && (
          <div className="mb-8" data-testid="pending-teams-section">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-500 mb-4">
              Pending Approval ({pendingTeams.length})
            </h3>
            <div className="space-y-3">
              {pendingTeams.map((team) => (
                <div
                  key={team.id}
                  className="bg-[#121212] border border-yellow-500/30 p-4 flex items-center justify-between"
                  data-testid={`pending-team-${team.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                      <span className="font-mono text-sm text-yellow-500 font-bold">{team.tag}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{team.name}</h4>
                      <p className="text-xs text-zinc-500">Captain: {team.captain_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleApproveTeam(team.id)}
                      className="bg-green-500 text-black hover:bg-green-400 rounded-none"
                      size="sm"
                      data-testid={`approve-team-btn-${team.id}`}
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleDeclineTeam(team.id)}
                      variant="outline"
                      className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none"
                      size="sm"
                      data-testid={`decline-team-btn-${team.id}`}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Teams Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : teams.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800" data-testid="no-teams-message">
            <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Teams Yet</h2>
            <p className="text-zinc-500 mb-6">Be the first to register your team!</p>
            {isAuthenticated && (
              <Button 
                onClick={() => setCreateOpen(true)}
                className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none"
              >
                Register Now
              </Button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => (
              <div 
                key={team.id}
                className="bg-[#121212] border border-white/5 p-6 card-hover relative cursor-pointer"
                data-testid={`team-card-${team.id}`}
                onClick={() => openTeamDetails(team)}
              >
                {/* Team header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    <span className="font-mono text-lg text-cyan-400 font-bold">
                      {team.tag}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-bold text-white truncate">
                      {team.name}
                    </h3>
                    <div className="flex items-center gap-1 text-sm text-zinc-500">
                      <Crown className="w-3 h-3 text-yellow-500" />
                      {team.captain_name}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {team.description && (
                  <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                    {team.description}
                  </p>
                )}

                {/* Members count */}
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <Users className="w-4 h-4" />
                  {team.members?.length || 1} member{(team.members?.length || 1) !== 1 ? 's' : ''}
                  <span className="text-zinc-700 ml-auto text-xs">Click to view</span>
                </div>

                {/* Availability */}
                {team.availability?.display && team.availability?.games?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 space-y-1" data-testid={`avail-${team.id}`}>
                    {team.availability.games.sort((a,b) => a.game - b.game).map(g => (
                      <div key={g.game} className="flex items-center gap-2">
                        <span className="text-cyan-400 text-xs font-mono font-bold w-16">Game {g.game}</span>
                        <span className="text-zinc-400 text-xs">{to12hr(g.time)} <span className="text-zinc-600">{team.availability.timezone || 'EST'}</span></span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Delete button for captain or admin */}
                {(user?.id === team.captain_id || isAdmin) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team.id); }}
                    className="absolute top-4 right-4 text-zinc-600 hover:text-red-500 transition-colors"
                    title="Delete team"
                    data-testid={`delete-team-${team.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Team Detail Dialog */}
        <Dialog open={!!selectedTeam && !addPlayerOpen} onOpenChange={() => setSelectedTeam(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="team-detail-dialog">
            {selectedTeam && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                      <span className="font-mono text-sm text-cyan-400 font-bold">{selectedTeam.tag}</span>
                    </div>
                    {selectedTeam.name}
                  </DialogTitle>
                </DialogHeader>

                {selectedTeam.description && (
                  <p className="text-zinc-400 text-sm mt-2">{selectedTeam.description}</p>
                )}

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">
                      Squad Members ({teamMembers.length})
                    </h3>
                    {isTeamLeader(selectedTeam) && (
                      <Button onClick={openAddPlayer} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none h-7 text-xs" data-testid="team-add-player-button">
                        <UserPlus className="w-3 h-3 mr-1" />Add Player
                      </Button>
                    )}
                  </div>

                  {membersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <p className="text-zinc-500 text-sm py-4">No members found</p>
                  ) : (
                    <div className="space-y-2">
                      {teamMembers.map((member) => (
                        <div
                          key={member.id}
                          className={`flex items-center gap-3 p-3 ${
                            member.role === 'captain' ? 'bg-yellow-500/5 border border-yellow-500/20' : 'bg-black border border-zinc-800'
                          }`}
                          data-testid={`member-${member.id}`}
                        >
                          <div className={`w-10 h-10 flex items-center justify-center ${
                            member.role === 'captain' ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-cyan-400/10 border border-cyan-400/30'
                          }`}>
                            <span className={`text-sm font-bold ${
                              member.role === 'captain' ? 'text-yellow-500' : 'text-cyan-400'
                            }`}>
                              {member.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium truncate">{member.name}</span>
                              {member.role === 'captain' && (
                                <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10">
                                  <Crown className="w-3 h-3 text-yellow-500" />
                                  <span className="text-yellow-500 text-xs font-bold uppercase">Leader</span>
                                </div>
                              )}
                            </div>
                            <span className="text-zinc-500 text-xs">{member.role === 'captain' ? 'Squad Leader' : 'Member'}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Circle className={`w-2.5 h-2.5 ${
                              member.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'
                            }`} />
                            <span className={`text-xs ${member.online ? 'text-green-500' : 'text-zinc-600'}`}>
                              {member.online ? 'Online' : 'Offline'}
                            </span>
                            {isTeamLeader(selectedTeam) && member.role !== 'captain' && (
                              <button onClick={() => handleRemovePlayer(member.id)} className="text-zinc-600 hover:text-red-500 transition-colors ml-1" title="Remove from roster" data-testid={`remove-member-${member.id}`}>
                                <UserMinus className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Availability section in detail */}
                {(isTeamLeader(selectedTeam) || isAdmin) && (
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Availability
                      </h3>
                      <Button onClick={openAvailability} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none h-7 text-xs" data-testid="set-avail-btn">
                        {selectedTeam.availability ? 'Edit' : 'Set'}
                      </Button>
                    </div>
                    {selectedTeam.availability?.games?.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {selectedTeam.availability.games.sort((a,b) => a.game - b.game).map(g => (
                          <div key={g.game} className="flex items-center gap-2">
                            <span className="text-cyan-400 text-xs font-mono font-bold w-16">Game {g.game}</span>
                            <span className="text-zinc-400 text-xs">{to12hr(g.time)} <span className="text-zinc-600">{selectedTeam.availability.timezone || 'EST'}</span></span>
                          </div>
                        ))}
                        {!selectedTeam.availability.display && <span className="text-zinc-600 text-xs ml-2">(hidden)</span>}
                      </div>
                    ) : (
                      <p className="mt-2 text-zinc-600 text-xs">Not set</p>
                    )}
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Availability Dialog */}
        <Dialog open={availOpen} onOpenChange={setAvailOpen}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-sm" data-testid="avail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">Set Availability</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveAvailability} className="space-y-4 mt-4">
              <p className="text-zinc-500 text-xs">Select games and set times:</p>
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6].map(num => {
                  const active = availGames.find(g => g.game === num);
                  return (
                    <div key={num} className="flex items-center gap-3">
                      <button type="button" onClick={() => toggleGame(num)}
                        className={`w-20 h-9 flex items-center justify-center text-xs font-bold font-mono border transition-colors ${
                          active ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400' : 'border-zinc-800 text-zinc-600 hover:text-zinc-400'
                        }`}
                        data-testid={`game-toggle-${num}`}
                      >
                        Game {num}
                      </button>
                      {active && (
                        <Input type="time" value={active.time} onChange={(e) => updateGameTime(num, e.target.value)}
                          className="bg-black border-zinc-800 text-white rounded-none h-9 flex-1 focus:border-cyan-400"
                          data-testid={`game-time-${num}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">Time Zone</Label>
                <select value={availTimezone} onChange={(e) => setAvailTimezone(e.target.value)}
                  className="w-full h-9 bg-black border border-zinc-800 text-white text-sm px-3 focus:border-cyan-400 outline-none"
                  data-testid="avail-timezone"
                >
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setAvailDisplay(!availDisplay)}
                  className={`w-10 h-6 rounded-full transition-colors relative ${availDisplay ? 'bg-cyan-400' : 'bg-zinc-800'}`}
                  data-testid="avail-display-toggle"
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${availDisplay ? 'left-5' : 'left-1'}`} />
                </button>
                <span className="text-zinc-400 text-sm">Display on team card</span>
              </div>
              <div className="flex gap-3">
                {selectedTeam?.availability?.games?.length > 0 && (
                  <Button type="button" onClick={handleRemoveAvailability} variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-none h-10" data-testid="remove-avail-btn">
                    Remove
                  </Button>
                )}
                <Button type="submit" className="flex-1 bg-cyan-400 text-black font-bold uppercase hover:bg-cyan-300 rounded-none h-10" data-testid="save-avail-btn">
                  Save
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Player to Team Dialog */}
        <Dialog open={addPlayerOpen} onOpenChange={setAddPlayerOpen}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="team-add-player-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">Add Player to Roster</DialogTitle>
            </DialogHeader>
            <Input
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              placeholder="Search players..."
              className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-10 focus:border-cyan-400 mt-4"
              data-testid="team-add-player-search"
            />
            <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
              {filteredAddPlayers.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-6">No available players found</p>
              ) : (
                filteredAddPlayers.map((player) => (
                  <button
                    key={player.id}
                    onClick={() => handleAddPlayer(player.id)}
                    className="w-full flex items-center gap-3 p-3 bg-black border border-zinc-800 hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-all text-left"
                    data-testid={`team-add-${player.id}`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center ${player.online ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800 border border-zinc-700'}`}>
                      <span className={`text-xs font-bold ${player.online ? 'text-green-500' : 'text-zinc-500'}`}>{player.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-white text-sm font-medium">{player.name}</span>
                      {player.team_name ? <span className="text-zinc-600 text-xs ml-2">[{player.team_tag}]</span> : <span className="text-zinc-600 text-xs ml-2">No clan</span>}
                    </div>
                    <Circle className={`w-2 h-2 ${player.online ? 'fill-green-500 text-green-500' : 'text-zinc-600'}`} />
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
